import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { normalizeDecimalInput } from '../../utils/sniperArm';
import { parseBulkPatch, type SniperBulkPatch } from '../../utils/sniperBulk';

export type { SniperBulkPatch };

/**
 * Bulk-edit bar for the markets SELECTED within one event card. The operator
 * types only the params they want to change (blank = leave untouched) and hits
 * Apply to write them to ALL selected markets in one request. Enable/Disable act
 * on the whole selection too. Add a new bulk param here + in `SniperBulkPatch`
 * and the wiring below — the shape is intentionally extensible.
 *
 * Validation mirrors `SniperMarketRow` and the backend DTO: size >= 1, maxPrice ∈
 * (0,1], gate 1..100 (→ 0..1). A field left blank is simply omitted from the
 * patch; Apply is enabled as long as at least one typed field is valid AND none
 * typed is invalid.
 */
export function SniperBulkBar({
  count,
  busy,
  onApplyParams,
  onSetEnabled,
  onClear,
}: {
  count: number;
  busy: boolean;
  /** Receives an `onApplied` callback — invoked ONLY once the write succeeds. */
  onApplyParams: (patch: SniperBulkPatch, onApplied: () => void) => void;
  onSetEnabled: (enabled: boolean) => void;
  onClear: () => void;
}) {
  const [size, setSize] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [gate, setGate] = useState('');

  const parsed = useMemo(
    () => parseBulkPatch(size, maxPrice, gate),
    [size, maxPrice, gate],
  );

  // Enable/Disable writes ONLY `{enabled}`. With params typed but not applied,
  // that would arm markets at their OLD size/gate while the bar still shows the
  // new values as if they had been written — fail closed and make the operator
  // Apply or clear first.
  const hasDrafts = size !== '' || maxPrice !== '' || gate !== '';

  const clearDrafts = () => {
    setSize('');
    setMaxPrice('');
    setGate('');
  };

  const apply = () => {
    if (!parsed.canApply || busy) return;
    // Drafts clear ONLY once the write lands. Clearing optimistically would drop
    // `hasDrafts` on a FAILED apply, re-opening Enable over params that were
    // never written — the exact footgun the gate exists to close.
    onApplyParams(parsed.patch, clearDrafts);
  };

  const fieldSx = {
    '& .MuiInputBase-input': { py: 0.35, px: 0.75, fontSize: 12 },
    '& .MuiInputLabel-root': { fontSize: 10.5 },
    '& .MuiInputLabel-shrink': { fontSize: 11 },
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        px: 1,
        py: 0.5,
        borderColor: 'primary.main',
        bgcolor: 'rgba(33,150,243,0.06)',
      }}
    >
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main' }}>
          {count} selected
        </Typography>
        <Box sx={{ width: 1, height: 18, borderLeft: 1, borderColor: 'divider', mx: 0.5 }} />

        <TextField
          size="small"
          label="Size"
          value={size}
          onChange={(e) => setSize(normalizeDecimalInput(e.target.value))}
          inputProps={{ inputMode: 'decimal', 'aria-label': 'bulk size' }}
          sx={{ width: 66, ...fieldSx }}
        />
        <TextField
          size="small"
          label="Max $"
          value={maxPrice}
          onChange={(e) => setMaxPrice(normalizeDecimalInput(e.target.value))}
          inputProps={{ inputMode: 'decimal', 'aria-label': 'bulk max price' }}
          sx={{ width: 66, ...fieldSx }}
        />
        <TextField
          size="small"
          label="Gate %"
          value={gate}
          onChange={(e) => setGate(e.target.value.replace(/[^0-9]/g, ''))}
          inputProps={{ inputMode: 'numeric', maxLength: 3, 'aria-label': 'bulk gate percent' }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontSize: 11 } }}>
                %
              </InputAdornment>
            ),
          }}
          sx={{ width: 80, ...fieldSx }}
        />
        <Button
          size="small"
          variant="contained"
          onClick={apply}
          disabled={!parsed.canApply || busy}
          sx={{ minWidth: 0, px: 1.25, fontSize: 12 }}
        >
          Apply
        </Button>

        <Box sx={{ width: 1, height: 18, borderLeft: 1, borderColor: 'divider', mx: 0.5 }} />
        <Tooltip
          title={
            hasDrafts
              ? 'Apply or clear the typed params first — Enable writes only the on/off flag'
              : 'Enable all selected markets'
          }
        >
          <span>
            <Button
              size="small"
              variant="outlined"
              onClick={() => onSetEnabled(true)}
              disabled={busy || hasDrafts}
              sx={{ minWidth: 0, px: 1, fontSize: 12 }}
            >
              Enable
            </Button>
          </span>
        </Tooltip>
        <Tooltip
          title={
            hasDrafts
              ? 'Apply or clear the typed params first — Disable writes only the on/off flag'
              : 'Disable all selected markets'
          }
        >
          <span>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={() => onSetEnabled(false)}
              disabled={busy || hasDrafts}
              sx={{ minWidth: 0, px: 1, fontSize: 12 }}
            >
              Disable
            </Button>
          </span>
        </Tooltip>

        <Box sx={{ flex: 1 }} />
        <Tooltip title="Clear selection">
          <span>
            <IconButton size="small" onClick={onClear} disabled={busy} aria-label="clear selection" sx={{ p: 0.5 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Paper>
  );
}
